import { installedIos } from "@adea/ui";
import { type Place, stepFor } from "./ios-history-steps.ts";

/* The installed iOS app keeps its history the way X does (ios-history-steps.ts): this puts that decision in front of
   every navigation the router commits (a Link, navigate(), the shell's own links), so no link is edited. Android,
   browser tabs and desktop: nothing is installed and the router behaves as before. */

type Loc = { pathname: string; searchStr: string; href: string; state?: unknown };
type Commit = (next: Loc & { replace?: boolean }) => Promise<void>;
type Router = {
  latestLocation: Loc & { state: { __TSR_index?: number } };
  commitLocation: Commit;
  history: {
    back: () => void;
    subscribe: (cb: (e: { location: Loc & { state: { __TSR_index?: number } } }) => void) => () => void;
  };
};

/** The flags that make Back close a side card or the connect panel: on a replaced entry there is nothing to go back to. */
const PUSHED_FLAGS = { sideCard: undefined, connectPanel: undefined };

export function installIosHistory(router: unknown): void {
  if (!installedIos()) return;
  const r = router as Router;
  const seen = new Map<number, Place>();
  const note = (l: Router["latestLocation"]) =>
    seen.set(l.state.__TSR_index ?? 0, { pathname: l.pathname, search: l.searchStr });
  note(r.latestLocation);
  r.history.subscribe(({ location }) => note(location));

  const commit = r.commitLocation.bind(r);
  r.commitLocation = (next) => {
    const index = r.latestLocation.state.__TSR_index ?? 0;
    const step = stepFor({
      from: { pathname: r.latestLocation.pathname, search: r.latestLocation.searchStr },
      index,
      to: { pathname: next.pathname, search: next.searchStr },
      previous: seen.get(index - 1) ?? null,
      replace: !!next.replace,
    });
    if (step === "back") {
      r.history.back();
      return Promise.resolve();
    }
    if (step === "push") return commit(next);
    return commit({
      ...next,
      replace: true,
      state: { ...(next.state as object | undefined), ...(next.replace ? {} : PUSHED_FLAGS) },
    });
  };
}
